import Vue from 'vue';
import jwtDecode from 'jwt-decode';
import { auth } from '@/services';
import store, { actions as appStoreActions} from '@/store';
import { actions as dashboardStoreActions } from 'dashboard-pdk/store';

class GlobalId {
    #ACCESS_TOKEN_KEY = 'AT';
    #REFRESH_TOKEN_KEY = 'RT';

    constructor() { };

    #secondsRemaining() {
        const token = this.tokenParsed;
        if (!token) return 0;

        const expiration = token.exp;
        if (!expiration) return 0;

        return expiration - Math.floor(new Date().getTime() / 1000);
    }

    #loadStore(){
        store.dispatch(dashboardStoreActions.LOCATION_GROUPS_LOAD);
        store.dispatch(appStoreActions.USER_GROUP_ROLES_LOAD);
        store.dispatch(appStoreActions.USER_PREFERENCES_LOAD);
        store.dispatch(appStoreActions.USER_DASHBOARD_PERMISSIONS_LOAD);
    }

    get token() {
        return sessionStorage.getItem(this.#ACCESS_TOKEN_KEY);
    }

    get tokenParsed() {
        const accessToken = this.token;
        if (accessToken) return jwtDecode(this.token);
        return {
            given_name: null,
            family_name: null,
            email: null,
            exp: null,
            iat: null,
            GlobalUserId: null
        };
    }

    get authenticated() {
        const secondsRemaining = this.#secondsRemaining();
        if (secondsRemaining <= 0) return false;

        return true;
    }

    setTokens(options) {
        sessionStorage.setItem(this.#ACCESS_TOKEN_KEY, options.accessToken);
        if (options.persistSession) { 
            localStorage.setItem(this.#REFRESH_TOKEN_KEY , options.refreshToken);
        }
        else {
            sessionStorage.setItem(this.#REFRESH_TOKEN_KEY, options.refreshToken);
        }

        this.#loadStore();
    }

    login(options) {
        if (options == null || options == undefined) {
            window.location = "/login";
        }
        else {
            window.location = `/login?redirect=${options.redirectUri}`;
        }
    }

    init(options) {
        return new Promise((resolve) => {
            var refreshToken = localStorage.getItem(this.#REFRESH_TOKEN_KEY );
            if (!refreshToken)
                refreshToken = sessionStorage.getItem(this.#REFRESH_TOKEN_KEY);

            if (!refreshToken) {
                resolve();
                return;
            }

            if (this.authenticated) {
                this.#loadStore();
                resolve();
                return;
            }

            this.updateToken(-1)
            .then(() => {
                this.#loadStore();
                resolve();
            });
                
        });
    }

    // minValidity in seconds. Optional, if not specified, 5 seconds.
    // If -1 is passed in, the token will be forcibly refreshed.
    updateToken(minValidity = 5) {
        return new Promise((resolve, reject) => {
            if (minValidity >= 0 && this.#secondsRemaining() > minValidity) {
                resolve(false);
                return;
            }

            var refreshToken = localStorage.getItem(this.#REFRESH_TOKEN_KEY );
            if (!refreshToken) 
                refreshToken = sessionStorage.getItem(this.#REFRESH_TOKEN_KEY);

            if (!refreshToken) {
                resolve(false);
                return;
            }

            auth.requestAccessToken({
                refreshToken
            }).then((accessToken) => {
                sessionStorage.setItem(this.#ACCESS_TOKEN_KEY, accessToken);
                resolve(true);
            }).catch((response) => {
                localStorage.removeItem(this.#REFRESH_TOKEN_KEY);
                reject(response);
            });
        });
    }

    clearToken() {
        //Clear the stores state.
        store.dispatch(dashboardStoreActions.CLEAR);
        store.dispatch(appStoreActions.LOGOUT);

        this.logout();
    }

    logout(options) {
        var refreshToken = localStorage.getItem(this.#REFRESH_TOKEN_KEY);
        if (!refreshToken)
            refreshToken = sessionStorage.getItem(this.#REFRESH_TOKEN_KEY);
        
        // Calling synchronously as fire & forget.
        auth.expireRefreshToken({ accessToken: this.token, refreshToken });

        // Remove tokens from local & session storage.
        localStorage.removeItem(this.#REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(this.#REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(this.#ACCESS_TOKEN_KEY);
        this.login();
    }
}


const _globalId = new GlobalId();

const Plugin = {
  install(Vue) {
    Vue.$auth = _globalId;
  }
};

Plugin.install = Vue => {
  Vue.$auth = _globalId;
  Object.defineProperties(Vue.prototype, {
    $auth: {
      get() {
        return _globalId;
      }
    }
  });
};

Vue.use(Plugin);